import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const { language, t } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const provider = searchParams.get('provider');
    const paypalToken = searchParams.get('token');

    if (provider === 'paypal' && paypalToken) {
      const capturePayPal = async () => {
        try {
          const res = await axios.post(`${API}/paypal/capture-order/${paypalToken}`, {}, {
            headers: getAuthHeaders(),
            withCredentials: true
          });
          if (res.data.status === 'COMPLETED') {
            setStatus('success');
            setPaymentInfo({ provider: 'paypal' });
          } else {
            setStatus('pending');
            setTimeout(capturePayPal, 3000);
          }
        } catch (err) {
          console.error('PayPal capture error:', err);
          setStatus('error');
        }
      };
      capturePayPal();
      return;
    }

    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        setPaymentInfo(res.data);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
        } else {
          setStatus('pending');
          setTimeout(checkStatus, 3000);
        }
      } catch (err) {
        console.error('Payment check error:', err);
        setStatus('error');
      }
    };

    checkStatus();
  }, [searchParams, getAuthHeaders]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="payment-success-page">
      <div className="max-w-md w-full mx-4">
        {status === 'loading' || status === 'pending' ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="payment-processing">
            <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-12 h-12 rounded-xl mx-auto mb-4" />
            <Loader2 className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Poppins' }}>
              {t('pricing_payment_processing')}
            </h2>
            <p className="text-sm text-slate-500">
              {language === 'fr' ? 'Veuillez patienter...' : 'Please wait...'}
            </p>
          </div>
        ) : status === 'success' ? (
          <div className="bg-white rounded-2xl border border-emerald-200 p-8 text-center" data-testid="payment-success">
            <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-12 h-12 rounded-xl mx-auto mb-4" />
            <div className="w-16 h-16 bg-emerald-50 rounded-full mx-auto mb-4 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Poppins' }}>
              {t('pricing_payment_success')}
            </h2>
            <p className="text-slate-500 mb-6">
              {t('pricing_activated')}
            </p>
            {paymentInfo && paymentInfo.amount_total && (
              <p className="text-sm text-slate-400 mb-6">
                {(paymentInfo.amount_total / 100).toFixed(2)}€ {paymentInfo.currency?.toUpperCase()}
              </p>
            )}
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2 active:scale-95 transition-transform"
              onClick={() => navigate('/dashboard')}
              data-testid="go-dashboard-btn"
            >
              {t('pricing_go_dashboard')} <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-red-200 p-8 text-center" data-testid="payment-error">
            <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Poppins' }}>
              {language === 'fr' ? 'Erreur de paiement' : 'Payment Error'}
            </h2>
            <p className="text-slate-500 mb-4">
              {language === 'fr' ? 'Le paiement n\'a pas pu etre verifie.' : 'Payment could not be verified.'}
            </p>
            <Button variant="outline" onClick={() => navigate('/pricing')} data-testid="retry-payment-btn">
              {language === 'fr' ? 'Retour aux tarifs' : 'Back to Pricing'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
